import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';
const BASE_URL = NAMESPACES.payments_api.payment_transactions;

const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Wrap with cache
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'payments_api',
  group: 'payment_transactions',
  defaultTtl: '30s',
  hashLargeSegments: true,
  emitMeta: true,
});

// /GetPaymentTransactionsByReceiptId/<int:receipt_id>

export const getPaymentTransactionsByReceiptId = async (receiptId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentTransactionsByReceiptId/${receiptId}`, undefined, { ttl: '1m' })
    );
}

// /GetPaymentTransactionsByLocalId/<int:local_id>

export const getPaymentTransactionsByLocalIdAndDateRange = async (
    localId: number, 
    startDate: string, 
    endDate: string
  ) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetPaymentTransactionsByLocalIdAndDateRange/${localId}/${startDate}/${endDate}`, undefined, { ttl: '1m' })
    );
  };

// /GetNotApprovedSessionTransactionsByLocalId/<int:local_id>

export const getNotApprovedSessionTransactionsByLocalId = async (localId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetNotApprovedSessionTransactionsByLocalId/${localId}`, undefined, { ttl: '30s' })
    );
}

// /GetSessionPaymentTransactionSummaryByLocalId/<int:local_id>

export const getSessionPaymentTransactionSummaryByLocalId = async (localId: number, sessionId?: number) => {
    const url = sessionId ? `/GetSessionPaymentTransactionSummaryByLocalId/${localId}/${sessionId}` : `/GetSessionPaymentTransactionSummaryByLocalId/${localId}`;
    return await retryWithBackoff(() =>
        cachedApi.get(url, undefined, { ttl: '1m' })
    );
}

// /GetTotalCashReceivedInSessionByLocalId/<int:local_id>

export const getTotalCashReceivedInSessionByLocalId = async (localId: number) => {
    return await retryWithBackoff(() =>
        cachedApi.get(`/GetTotalCashReceivedInSessionByLocalId/${localId}`, undefined, { ttl: '1m' })
    );
}


// Interface for splitting a transaction into multiple payment methods
interface SplitPaymentMethod {
    payment_type_id: number;
    amount: number;
    custom_payment_type_id?: number;
    tip_amount?: number;
}

// MODE 1: Single payment type change (backward compatible)
export const changeTransactionPaymentType = async (
    transactionId: number, 
    paymentTypeId: number,
    customPaymentTypeId?: number
) => {
    const res = await retryWithBackoff(() =>
        api.put(`/ChangeTransactionPaymentType/${transactionId}`, {
            payment_type_id: paymentTypeId,
            custom_payment_type_id: customPaymentTypeId
        })
    );
    await cachedApi.clear();
    return res;
}

// MODE 2: Split transaction into multiple payment methods
export const splitTransactionPaymentType = async (
    transactionId: number,
    multiplePaymentMethods: SplitPaymentMethod[]
) => {
    const res = await retryWithBackoff(() =>
        api.put(`/ChangeTransactionPaymentType/${transactionId}`, {
            multiple_payment_methods: multiplePaymentMethods
        })
    );
    await cachedApi.clear();
    return res;
}

export const deletePaymentTransaction = async (transactionId: number) => {
    const res = await retryWithBackoff(() =>
        api.delete(`/DeletePaymentTransaction/${transactionId}`)
    );
    await cachedApi.clear();
    return res;
}